<template>
    <div class="flt-r">
        <button type="button" class="btn" :disabled="disabled" v-on:click="modal?.show()">
            Write Off Time Entries
        </button>
        <div ref="modal" class="modal" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog" role="document">
                <form class="modal-content" v-on:submit.prevent="submit">
                    <div class="block block-rounded block-transparent mb-0">
                        <div class="block-header block-header-default">
                            <h3 class="block-title">Write Off Time Entry Cost</h3>
                            <div class="block-options">
                                <button type="button" class="btn-block-option" data-bs-dismiss="modal" aria-label="Close">
                                    <i class="fa fa-fw fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <div v-if="loading" class="block-content text-center">
                            <p class="h4 mb-4">Loading</p>
                        </div>
                        <div v-show="loading === false" class="block-content fs-sm">
                            <template v-if="total === null">
                                <div class="mb-4">
                                    <label class="form-label" for="f_amount">Amount</label>
                                    <input type="text" class="form-control" :class="{'is-invalid': errors.amount}" id="f_amount" v-model="amount">

                                    <div v-if="errors.amount" class="invalid-feedback">{{ errors.amount.join(', ') }}</div>
                                </div>

                                <div class="mb-4">
                                    <label class="form-label mt-1" for="f_useExistingInvoice">Allocate To Existing Invoice</label>
                                    <div class="input-group">
                                        <div class="form-check mt-2">
                                            <input type="checkbox" class="form-check-input" :class="{'is-invalid': errors.useExistingInvoice}" id="f_useExistingInvoice" v-model="useExistingInvoice">
                                        </div>
                                        <div class="flex-grow-1">
                                            <v-select
                                                :options="invoiceOptions"
                                                v-on:search="searchInvoices"
                                                v-model="existingInvoiceID"
                                                label="text"
                                                :disabled="useExistingInvoice === false"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div class="mb-4" v-else-if="completed === false">
                                <p>The system can create an invoice for &pound;{{ total }}. Press submit to continue.</p>
                            </div>
                            <div class="mb-4" v-else>
                                <p>That has been done. Please refresh the page.</p>
                            </div>
                        </div>
                        <div class="block-content block-content-full text-end bg-body">
                            <button type="button" class="btn btn-sm btn-alt-secondary me-1" data-bs-dismiss="modal">Close</button>
                            <button v-if="!loading && !completed" type="submit" class="btn btn-sm btn-success">Submit</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Modal from 'bootstrap/js/dist/modal';
import VSelect from 'vue-select';

const CancelToken = axios.CancelToken;

import { debounce } from 'lodash';

export default {
    components: {
        VSelect,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        invoiceRoute: String,
        route: String,
    },
    data () {
        return {
            amount: null,
            completed: false,
            useExistingInvoice: false,
            invoiceOptions: [],
            invoiceSearch: null,
            existingInvoiceID: null,
            errors: {},
            loading: false,
            modal: null,
            total: null,
        };
    },
    created () {
        // "dereference" the debounced function by
        // not debouncing in function definition
        this.searchInvoices = debounce(this.searchInvoices, 350);
    },
    mounted () {
        this.modal = new Modal(this.$refs.modal);

        this.$refs.modal.addEventListener('hidden.bs.modal', () => {
            this.amount = null;
            this.completed = false;
            this.errors = {};
            this.total = null;
        });

        this.$refs.modal.addEventListener('show.bs.modal', () => {
            // initialise list
            this.searchInvoices();
        });
    },
    watch: {
        useExistingInvoice(value) {
            if (value === false) {
                this.existingInvoiceID = null;
            }
        },
    },
    methods: {
        searchInvoices: async function(searchTerm, loading) {
            loading = loading || function () {};

            this.invoiceOptions = [];
            loading(true);

            if (this.invoiceSearch !== null) {
                this.invoiceSearch.cancel('Operation canceled by the user.');
            }
            this.invoiceSearch = CancelToken.source();

            try {
                const response = await axios.get(this.invoiceRoute, {
                    params: {
                        search: searchTerm,
                    },
                    cancelToken: this.invoiceSearch.token,
                });

                this.invoiceOptions = response.data.data;
            } catch (err) {
                console.error(err);
            } finally {
                loading(false);
            }
        },
        submit () {
            if (this.completed === true) {
                return;
            }

            this.loading = true;

            axios.post(this.route, {
                amount: this.amount,
                total: this.total,
                useExistingInvoice: this.useExistingInvoice,
                invoiceID: this.existingInvoiceID?.id,
            }).then(({ data }) => {
                this.completed = data.completed;
                this.total = data.total;
            }).catch((error) => {
                let status = 'Unknown';
                if (error.response) {
                    status = error.response.status;
                }

                if (status === 422) {
                    this.errors = error.response.data.errors;
                    return;
                }

                let message = 'System Error';
                if (error.response && error.response.message) {
                    message = error.response.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Could not write off Time Entries',
                    text: message,
                });
            }).finally(() => {
                this.loading = false;
            });
        },
    },
};
</script>

<style scoped>
    .flt-r {
        float:right;
    }
</style>
